import { Action, createReducer, on, createFeatureSelector, createSelector } from '@ngrx/store';
import { UserCustomizationActions } from './../actions';
import { Logo } from '@app/shared/models/logo';
import { Background } from '@app/shared/models/background';
import { DomainActions, TeamActions, SystemActions } from '@app/core/actions';
import { GetDomainCustomizationOutput } from '@smash-sdk/domain/01-2024';

export const customizationFeatureKey = 'userCustomization';

export interface State {
  customizationReceiver: GetDomainCustomizationOutput['customization'];
  customizationReceiverLoading: boolean;
  customizationReceiverError: boolean;
  customizationReceiverIsSystem: boolean;

  customizationSender: GetDomainCustomizationOutput['customization'];
  customizationSenderLoading: boolean;
  customizationSenderError: boolean;
  customizationSenderIsSystem: boolean;

  customizationToUpdate: GetDomainCustomizationOutput['customization'];
  customizationUpdating: boolean;
  customizationUpdated: boolean;
  customizationUpdatingError: any;


  backgrounds: Background[];
  backgroundsLoading: boolean;
  backgroundsError: any;

  logosToCreate: any[];
  logosToDelete: Logo[];
  backgroundsToCreate: any[];
  backgroundsToDelete: Background[];

  logos: Logo[];
  logosLoading: boolean;
  logosError: any;

  selectedBackground: Background;
  selectedLogo: Logo;

}

export const initialState: State = {
  customizationReceiver: null,
  customizationReceiverLoading: false,
  customizationReceiverError: null,
  customizationReceiverIsSystem: false,

  customizationSender: null,
  customizationSenderLoading: false,
  customizationSenderError: null,
  customizationSenderIsSystem: false,


  customizationToUpdate: null,
  customizationUpdating: false,
  customizationUpdated: false,
  customizationUpdatingError: null,

  backgrounds: [],
  backgroundsLoading: false,
  backgroundsError: null,

  logosToCreate: [],
  logosToDelete: [],
  backgroundsToCreate: [],
  backgroundsToDelete: [],

  logos: [],
  logosLoading: false,
  logosError: null,

  selectedBackground: null,
  selectedLogo: null,
};

const customizationReducer = createReducer(
  initialState,
  // Recipient customization
  on(TeamActions.loadTeamCustomization, SystemActions.loadManagedCustomization, (state) => ({
    ...state,
    customizationReceiver: initialState.customizationReceiver,
    customizationReceiverLoading: true,
    customizationReceiverError: initialState.customizationReceiverError,
    customizationReceiverIsSystem: initialState.customizationReceiverIsSystem,
    customizationToUpdate: initialState.customizationToUpdate
  })),
  on(TeamActions.loadTeamCustomizationSuccess, (state, { customization }) => ({
    ...state,
    customizationReceiver: customization,
    customizationToUpdate: customization,
    customizationReceiverLoading: false,
    selectedBackground: customization.background,
    selectedLogo: customization.logo,
  })),
  on(SystemActions.loadManagedCustomizationSuccess, (state, { customization }) => ({
    ...state,
    customizationReceiver: customization,
    customizationToUpdate: customization,
    customizationReceiverLoading: false,
    selectedBackground: customization.background,
    selectedLogo: customization.logo,
    customizationReceiverIsSystem: true
  })),
  on(TeamActions.loadTeamCustomizationFailure, SystemActions.loadManagedCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationReceiver: initialState.customizationReceiver,
    customizationReceiverLoading: false,
    customizationReceiverError: error,
  })),

  on(DomainActions.updateDomainCustomization, (state) => ({
    ...state,
    customizationUpdating: true,
    customizationUpdated: initialState.customizationUpdated,
    customizationUpdatingError: initialState.customizationUpdatingError,
  })),
  on(DomainActions.updateDomainCustomizationSuccess, (state) => ({
    ...state,
    customizationUpdated: true,
    ...initialState
  })),
  on(DomainActions.updateDomainCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationUpdating: false,
    customizationUpdatingError: error,
  })),



  on(UserCustomizationActions.updateTeamCustomization, (state) => ({
    ...state,
    customizationUpdating: true,
    customizationUpdated: initialState.customizationUpdated,
    customizationUpdatingError: initialState.customizationUpdatingError,
  })),
  on(UserCustomizationActions.updateCustomizationSuccess, (state) => ({
    ...state,
    customizationUpdated: true,
    ...initialState
  })),
  on(UserCustomizationActions.updateTeamCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationUpdating: false,
    customizationUpdatingError: error,
  })),



  on(TeamActions.loadTeamBackgrounds, (state) => ({
    ...state,
    backgrounds: initialState.backgrounds,
    backgroundsLoading: true,
    backgroundsError: initialState.backgroundsError,
  })),
  on(TeamActions.loadTeamBackgroundsSuccess, (state, { backgrounds }) => ({
    ...state,
    backgrounds,
    backgroundsLoading: false,
    backgroundsError: initialState.backgroundsError,
  })),
  on(TeamActions.loadTeamBackgroundsFailure, (state, { error }) => ({
    ...state,
    backgroundsLoading: false,
    backgroundsError: error,
  })),
  on(TeamActions.loadTeamLogos, (state) => ({
    ...state,
    logos: initialState.logos,
    logosLoading: true,
    logosError: initialState.logosError,
  })),
  on(TeamActions.loadTeamLogosSuccess, (state, { logos }) => ({
    ...state,
    logos,
    logosLoading: false,
    logosError: initialState.logosError,
  })),
  on(TeamActions.loadTeamLogosFailure, (state, { error }) => ({
    ...state,
    logosLoading: false,
    logosError: error,
  })),


  on(UserCustomizationActions.selectBackground, (state, { background }) => ({
    ...state,
    selectedBackground: background,
    customizationToUpdate: { ...state.customizationToUpdate, background },
  })),
  on(UserCustomizationActions.selectLogo, (state, { logo }) => ({
    ...state,
    selectedLogo: logo,
    customizationToUpdate: { ...state.customizationToUpdate, logo },
  })),
  on(UserCustomizationActions.addTeamLogoSuccess, (state, { logo }) => ({
    ...state,
    logos: [...state.logos, logo],
  })),
  on(UserCustomizationActions.addTeamLogoToCreate, (state, { logo }) => ({
    ...state,
    logosToCreate: [...state.logosToCreate, logo],
  })),
  on(UserCustomizationActions.addTeamBackgroundImageToCreateSuccess, (state, { background }) => ({
    ...state,
    backgroundsToCreate: [...state.backgroundsToCreate, background],
    backgrounds: [...state.backgrounds, background],
    customizationToUpdate: { ...state.customizationToUpdate, background },
    selectedBackground: background,
  })),
  on(UserCustomizationActions.addTeamBackgroundVideoSuccess, (state, { background }) => ({
    ...state,
    customizationToUpdate: { ...state.customizationToUpdate, background: background as GetDomainCustomizationOutput['customization']['background'] }, // FIX ME cast
    backgrounds: [...state.backgrounds, background],
    selectedBackground: background,
  })),
  on(UserCustomizationActions.addTeamLogoToCreateSuccess, (state, { logo }) => ({
    ...state,
    logosToCreate: [...state.backgroundsToCreate, logo],
    logos: [...state.logos, logo],
    customizationToUpdate: { ...state.customizationToUpdate, logo },
    selectedLogo: logo,
  })),
  on(UserCustomizationActions.addTeamBackgroundToDelete, (state, { background }) => ({
    ...state,
    backgroundsToDelete: [...state.backgroundsToDelete, background],
    backgrounds: state.backgrounds.filter(_background => background.id !== _background.id),
  })),
  on(UserCustomizationActions.addTeamLogoToDelete, (state, { logo }) => ({
    ...state,
    logosToDelete: [...state.logosToDelete, logo],
    logos: state.logos.filter(_logo => logo.id !== _logo.id),
  })),
  on(UserCustomizationActions.addTeamBackgroundImageSuccess, (state, { background }) => ({
    ...state,
    customizationToUpdate: { ...state.customizationToUpdate, background },
    backgrounds: [...state.backgrounds, background],
    selectedBackground: background,
  })),


  // Sender customization
  on(DomainActions.loadDomainCustomization, (state) => ({
    ...state,
    customizationSender: initialState.customizationSender,
    customizationSenderLoading: true,
    customizationSenderError: initialState.customizationSenderError,
    customizationSenderIsSystem: initialState.customizationSenderIsSystem,
    customizationToUpdate: initialState.customizationToUpdate
  })),
  on(DomainActions.loadDomainCustomizationSuccess, (state, { customization }) => ({
    ...state,
    customizationSender: customization,
    customizationToUpdate: customization,
    customizationSenderLoading: false,
    selectedBackground: customization.background,
    selectedLogo: customization.logo,
  })),
  on(DomainActions.loadDomainCustomizationFailure, (state, { error }) => ({
    ...state,
    customizationSender: initialState.customizationSender,
    customizationSenderLoading: false,
    customizationSenderError: error,
  })),

  on(DomainActions.addNewLogoToDomainCustomizationSuccess, (state, { logo }) => {
    return {
      ...state,
      customizationToUpdate: { ...state.customizationToUpdate, logo: { ...state.customizationToUpdate.logo, sourceUrl: logo.downloadUrl, thumbnail: logo.downloadUrl, type: 'Image' as GetDomainCustomizationOutput['customization']['logo']['type'] } },
    }
  }),

  on(DomainActions.addNewBackgroundImageToDomainCustomizationSuccess, (state, { background }) => {
    const newCustomization = { ...state.customizationToUpdate, background: { ...state.customizationToUpdate.background, sourceUrl: background.downloadUrl, type: 'Image' as GetDomainCustomizationOutput['customization']['background']['type'] } };
    return {
      ...state,
      customizationToUpdate: newCustomization,
    };
  }),
  on(DomainActions.addNewBackgroundVideoToDomainCustomizationSuccess, (state, { video }) => {
    const newCustomization = { ...state.customizationToUpdate, background: { sourceId: video.id, type: video.type as GetDomainCustomizationOutput['customization']['background']['type'], sourceUrl: video.url } };
    return {
      ...state,
      customizationToUpdate: newCustomization as GetDomainCustomizationOutput['customization'], // FIX ME cast
    };
  }),

);


export const getCustomizationState = createFeatureSelector<State>('userCustomization');

export const getCustomizationReceiver = createSelector(
  getCustomizationState,
  (state: State) => state.customizationReceiver
);

export const getCustomizationSender = createSelector(
  getCustomizationState,
  (state: State) => state.customizationSender
);

export const getCustomizationReceiverIsSystem = createSelector(
  getCustomizationState,
  (state: State) => state.customizationReceiverIsSystem
);

export const getTeamBackgrounds = createSelector(
  getCustomizationState,
  (state: State) => state.backgrounds
);

export const getTeamLogos = createSelector(
  getCustomizationState,
  (state: State) => state.logos
);

export const getSelectedBackground = createSelector(
  getCustomizationState,
  (state: State) => state.selectedBackground
);

export const getSelectedLogo = createSelector(
  getCustomizationState,
  (state: State) => state.selectedLogo
);

export const getCustomizationToUpdate = createSelector(
  getCustomizationState,
  (state: State) => state.customizationToUpdate
);

export const getCustomizationUpdating = createSelector(
  getCustomizationState,
  (state: State) => state.customizationUpdating
);

export const getCustomizationUpdated = createSelector(
  getCustomizationState,
  (state: State) => state.customizationUpdated
);

export const getLogosToCreate = createSelector(
  getCustomizationState,
  (state: State) => state.logosToCreate
);
export const getBackgroundsToCreate = createSelector(
  getCustomizationState,
  (state: State) => state.backgroundsToCreate
);
export const getLogosToDelete = createSelector(
  getCustomizationState,
  (state: State) => state.logosToDelete
);

export const getBackgroundToDelete = createSelector(
  getCustomizationState,
  (state: State) => state.backgroundsToDelete
);

export function reducer(state: State | undefined, action: Action) {
  return customizationReducer(state, action);
}


